/**
 * The month calendar widget of the dashboard's right rail (the mockup's react-day-picker
 * widget ported to the repo's zero-extra-dependency idiom): a Monday-first six-week grid, real
 * month navigation and a dot under every day that carries events. A day counts as "with
 * events" from the same fetched window the meetings list uses - no extra requests.
 */

import { useMemo } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { dayKey, monthGrid, sameDay } from './month';

export interface CalendarWidgetProps {
  /** Displayed month (any date inside it). */
  readonly month: Date;
  readonly onMonthChange: (month: Date) => void;
  /** Selected day; undefined when nothing is selected. */
  readonly selected: Date | undefined;
  readonly onSelect: (day: Date | undefined) => void;
  /** Local days carrying at least one event (yyyy-mm-dd keys). */
  readonly eventDays: ReadonlySet<string>;
  /** Today, injected for determinism in tests. */
  readonly today: Date;
}

const WEEKDAY_COLUMNS = 7 as const;

export function CalendarWidget({
  month,
  onMonthChange,
  selected,
  onSelect,
  eventDays,
  today,
}: CalendarWidgetProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';

  const cells = useMemo(() => monthGrid(month.getFullYear(), month.getMonth()), [month]);

  const monthTitle = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(
    month,
  );
  const weekdayLabels = useMemo(() => {
    // The grid is Monday-first: derive the localized column captions from a known Monday.
    const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    return Array.from({ length: WEEKDAY_COLUMNS }, (_, index) =>
      formatter.format(new Date(2024, 0, 1 + index)),
    );
  }, [locale]);

  function shiftMonth(delta: number): void {
    onMonthChange(new Date(month.getFullYear(), month.getMonth() + delta, 1));
  }

  return (
    <div className="rounded-xl border border-border bg-card pb-2" data-testid="calendar-widget">
      <div className="flex h-9 items-center justify-between px-2 pt-1">
        <button
          type="button"
          aria-label={t('calendar.widget.prev_month')}
          onClick={() => shiftMonth(-1)}
          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <ChevronLeft className="size-4" />
        </button>
        <span
          className="text-[13px] font-semibold capitalize text-foreground"
          data-testid="calendar-widget-month"
        >
          {monthTitle}
        </span>
        <button
          type="button"
          aria-label={t('calendar.widget.next_month')}
          onClick={() => shiftMonth(1)}
          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>

      <table className="w-full border-separate border-spacing-0 px-1" role="grid">
        <thead>
          <tr>
            {weekdayLabels.map((label) => (
              <th
                key={label}
                scope="col"
                className="h-7 text-center text-[10.5px] font-normal text-muted-foreground"
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 6 }, (_, week) => (
            <tr key={week}>
              {cells.slice(week * 7, week * 7 + 7).map((cell) => {
                const inMonth = cell.getMonth() === month.getMonth();
                const key = dayKey(cell);
                const hasEvents = eventDays.has(key);
                const isSelected = selected !== undefined && sameDay(cell, selected);
                const isToday = sameDay(cell, today);
                return (
                  <td key={key} className="relative h-9 p-0 text-center align-middle">
                    <button
                      type="button"
                      onClick={() => onSelect(cell)}
                      aria-pressed={isSelected}
                      aria-label={new Intl.DateTimeFormat(locale, {
                        day: 'numeric',
                        month: 'long',
                      }).format(cell)}
                      className={
                        isSelected
                          ? 'mx-auto flex size-7 items-center justify-center rounded-full bg-primary text-[12px] font-semibold text-primary-foreground'
                          : isToday
                            ? 'mx-auto flex size-7 items-center justify-center rounded-full border border-primary/60 text-[12px] font-medium text-foreground'
                            : inMonth
                              ? 'mx-auto flex size-7 items-center justify-center rounded-full text-[12px] text-foreground transition-colors hover:bg-accent'
                              : 'mx-auto flex size-7 items-center justify-center rounded-full text-[12px] text-muted-foreground/40 transition-colors hover:bg-accent/50'
                      }
                    >
                      {cell.getDate()}
                      {hasEvents ? (
                        <span
                          data-testid={`event-dot-${key}`}
                          aria-hidden
                          className="absolute bottom-[3px] left-1/2 size-1 -translate-x-1/2 rounded-full bg-emerald-500"
                        />
                      ) : null}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex items-center gap-1.5 px-3 pt-1.5 text-[10.5px] font-medium text-muted-foreground">
        <span aria-hidden className="size-1 rounded-full bg-emerald-500" />
        {t('calendar.widget.legend')}
      </div>
    </div>
  );
}
